import { defineStore, acceptHMRUpdate } from "pinia"
import type { AxiosResponse } from "axios"
import i18n from "@/utils/i18n"
import { fetchPromiseAll, simpleAxios } from "@/utils/http"
import { syncServerClockFromPayload } from "@/utils/serverClock"
import { mapLanguageValueToKey } from "@/lang/config"
import { getTranslatedObject } from "@/utils/translate"
import { useContentStore } from "./content"
import { useEventsStore } from "./events"
import {
    resolveReactionSetById,
    resolveCommonSenseReactions,
    type CommonSenseReaction,
} from "@/constants/common_sense_reactions"
import {
    COMMON_SENSE_DATA_KEYS,
    challengeDataLangFallbackKeys,
    hasCommonSenseChallengeSteps,
    mergeTimelineOrderContextFromLanguageFallbacks,
} from "@/constants/common_sense_cms"
import { fetchMediaAsFile } from "@/utils/fetchMediaAsFile"
import type {
    CommonSenseActivityConfiguration,
    CommonSenseChallengeOption,
    CommonSenseData,
    CommonSenseExtraTeamInformation,
    CommonSenseSetting,
    CommonSenseSettingOption,
    CommonSenseTimer,
} from "./interfaces/common_sense_interfaces"

export type CommonSenseFloatingReaction = {
    uid: string
    reactionId: string
    place: number
    teamId: number | null
}

export type CommonSenseFloatingPoint = {
    uid: string
    points: number
    place: number
    teamId: number
}

let lastReactionSend: { key: string; at: number } | null = null

/**
 * Common Sense — client-side model of the server state machine (`CommonSenseData`).
 *
 * 1) **`status`** — session lifecycle (`COMMON_SENSE_GAME_STATUS`): not started vs running.
 * 2) **`in_challenge_status`** — high-level step within the current challenge (`IN_CHALLENGE_PHASE`).
 * 3) **`challenge_play_subphase`** — when in `CHALLENGE_PLAYING`, which inner step is shown (quiz 1, quiz 2, …).
 */

/** `CommonSenseData.status` — session lifecycle (orthogonal to `in_challenge_status`). */
export const COMMON_SENSE_GAME_STATUS = {
    /** Not started — waiting for instructor to start. */
    IDLE: 0,
    /** Running. */
    RUNNING: 1,
    /** Final game ranking is being shown. */
    FINISHED: 2,
} as const

export type CommonSenseGameStatus = (typeof COMMON_SENSE_GAME_STATUS)[keyof typeof COMMON_SENSE_GAME_STATUS]

/**
 * `CommonSenseData.in_challenge_status` — flow for the active challenge round.
 * Meaningful once the game is running and (usually) `challenge_active` is set.
 */
export const IN_CHALLENGE_PHASE = {
    /** Waiting for the GM to pick / commit a challenge. */
    WAITING_GM_CHALLENGE: 0,
    /** Roulette rolling. */
    ROLLING: 1,
    /** Showing challenge rules on public / briefing screens. */
    SHOWING_RULES: 2,
    /** Teams are playing the challenge. */
    CHALLENGE_PLAYING: 3,
    /** Showing the result of the challenge. */
    CHALLENGE_RESULT: 4,
    /** Challenge finished. SHOW RANKING OF CHALLENGE RESULTS */
    CHALLENGE_FINISHED: 5,
} as const

export type InChallengePhase = (typeof IN_CHALLENGE_PHASE)[keyof typeof IN_CHALLENGE_PHASE]

/**
 * `CommonSenseData.challenge_play_subphase` — while `in_challenge_status === CHALLENGE_PLAYING`,
 * which inner phase of a multi-step challenge is active (1 = first quiz screen, 2 = second, …).
 * Use `0` when the challenge has no sub-steps or the field is not used.
 */
export const CHALLENGE_PLAY_SUBPHASE_IDLE = 0

/** Play UI variant from CMS `common_sense_challenge.type`. */
export type CommonSenseChallengeType = "fair_prize" | "quiz" | "timeline"

export function resolveActiveChallengeId(active: unknown): number | null {
    if (active == null || active === "") return null
    if (typeof active === "number" && Number.isFinite(active)) return active
    if (typeof active === "object" && active !== null && "id" in active) {
        const n = Number((active as { id: unknown }).id)
        return Number.isFinite(n) ? n : null
    }
    if (typeof active === "string" && active !== "") {
        const n = Number(active)
        return Number.isFinite(n) ? n : null
    }
    return null
}

export function normalizeCommonSenseChallengeIds(raw: unknown): number[] {
    if (!Array.isArray(raw)) return []
    const out: number[] = []
    const seen = new Set<number>()
    for (const item of raw) {
        let n = NaN
        if (typeof item === "number" && Number.isFinite(item)) n = item
        else if (typeof item === "string" && item !== "") {
            const parsed = Number(item)
            if (Number.isFinite(parsed)) n = parsed
        } else if (item && typeof item === "object" && "id" in item) {
            const parsed = Number((item as { id: unknown }).id)
            if (Number.isFinite(parsed)) n = parsed
        }
        if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
            out.push(n)
            seen.add(n)
        }
    }
    return out
}

export function resolveCommonSenseFlowCompletedIds(
    commonSenseData: CommonSenseData | null | undefined
): number[] {
    const pdata = commonSenseData?.player_data
    if (!pdata || typeof pdata !== "object" || Array.isArray(pdata)) return []
    return normalizeCommonSenseChallengeIds(
        (pdata as Record<string, unknown>).flow_completed_challenge_ids
    )
}

export function resolveCommonSenseFlowOrder(
    rawOrder: unknown,
    allowedIds: number[],
    options: { prependMissing?: boolean } = {}
): number[] {
    const allowed = normalizeCommonSenseChallengeIds(allowedIds)
    const allowedSet = new Set(allowed)
    const ordered = normalizeCommonSenseChallengeIds(rawOrder).filter((id) => allowedSet.has(id))
    const seen = new Set(ordered)
    const missing = allowed.filter((id) => !seen.has(id))
    return options.prependMissing ? [...missing, ...ordered] : [...ordered, ...missing]
}

export function normalizeCommonSenseFlowBoosters(
    raw: unknown,
    allowedIds?: number[]
): Record<string, string> {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {}
    const allowedSet = Array.isArray(allowedIds) ? new Set(allowedIds.map((id) => Number(id))) : null
    const out: Record<string, string> = {}
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        const challengeId = Number(key)
        if (!Number.isFinite(challengeId) || challengeId <= 0) continue
        if (allowedSet && !allowedSet.has(challengeId)) continue
        if (typeof value !== "string" || !value.trim()) continue
        out[String(challengeId)] = value.trim()
    }
    return out
}

/** Maps `CommonSenseQuestions.type` to the play UI variant. */
export function resolveChallengeTypeFromChallenge(
    challenge: Record<string, unknown> | null | undefined
): CommonSenseChallengeType {
    if (!challenge) return "fair_prize"

    const raw = String(challenge.type ?? "")
        .trim()
        .toUpperCase()
        .replace(/-/g, "_")

    if (raw === "QUIZ") return "quiz"
    if (raw === "TIMELINE") return "timeline"
    if (raw === "FAIR_PRIZE") return "fair_prize"

    const lower = raw.toLowerCase()
    if (lower.includes("quiz")) return "quiz"
    if (lower.includes("timeline") || lower.includes("cronolog") || lower.includes("chronolog")) {
        return "timeline"
    }
    if (lower.includes("fair") || lower.includes("price") || lower.includes("prize")) {
        return "fair_prize"
    }

    return "fair_prize"
}

export function inChallengePhaseIs(phase: unknown, expected: InChallengePhase): boolean {
    return Number(phase ?? 0) === expected
}

/** i18n key under `gm.common_sense` for a numeric `in_challenge_status`. */
export function inChallengePhaseI18nKey(phase: unknown): string {
    const p = Number(phase ?? 0)
    const keys: Record<number, string> = {
        [IN_CHALLENGE_PHASE.WAITING_GM_CHALLENGE]: "in_challenge_phase_waiting",
        [IN_CHALLENGE_PHASE.ROLLING]: "in_challenge_phase_rolling",
        [IN_CHALLENGE_PHASE.SHOWING_RULES]: "in_challenge_phase_showing_rules",
        [IN_CHALLENGE_PHASE.CHALLENGE_PLAYING]: "in_challenge_phase_playing",
        [IN_CHALLENGE_PHASE.CHALLENGE_RESULT]: "in_challenge_phase_result",
        [IN_CHALLENGE_PHASE.CHALLENGE_FINISHED]: "in_challenge_phase_ranking",
    }
    return keys[p] ?? "in_challenge_phase_unknown"
}

/**
 * Resolve challenge `data_*` from event language (es/en/lang1…) via organization mapping.
 */
export function resolveChallengeDataForEventLanguage(
    record: Record<string, unknown> | null | undefined,
    eventLanguage: string | null | undefined
): unknown {
    if (!record) return null
    const langKey = mapLanguageValueToKey(eventLanguage)
    const type = record.type
    let primary: unknown = null
    const fallbacks: unknown[] = []

    for (const key of challengeDataLangFallbackKeys(langKey)) {
        const payload = record[`data_${key}`]
        if (!hasCommonSenseChallengeSteps(payload, type)) continue
        if (!primary) {
            primary = payload
            continue
        }
        fallbacks.push(payload)
    }

    if (!primary) return null
    return mergeTimelineOrderContextFromLanguageFallbacks(primary, fallbacks, type)
}

/** Number of inner steps in the active challenge `data` payload (matches backend advance logic). */
export function countCommonSenseChallengeSubphases(data: unknown): number {
    if (data == null) return 0
    if (Array.isArray(data)) return data.length
    if (typeof data !== "object") return 0
    const record = data as Record<string, unknown>
    for (const key of COMMON_SENSE_DATA_KEYS) {
        const value = record[key]
        if (Array.isArray(value) && value.length > 0) return value.length
    }
    return Object.keys(record).length
}

/**
 * Public display bridge: roulette vs challenge rules panel, derived from `in_challenge_status`.
 *
 * - **ROLLING** or **WAITING_GM_CHALLENGE** → roulette stage.
 * - **SHOWING_RULES** → rules panel.
 * - **ROLLING + `rouletteSpinCompletedForPublic`** → rules panel (legacy bridge after handoff starts; host phase should be SHOWING_RULES).
 */
export function resolvePublicChallengeDisplayFlags(
    inChallengeStatus: unknown,
    options?: { rouletteSpinCompletedForPublic?: boolean }
): { showCarousel: boolean; showRulesPanel: boolean } {
    const p = Number(inChallengeStatus ?? 0)
    const spinBridge = options?.rouletteSpinCompletedForPublic === true

    if (p === IN_CHALLENGE_PHASE.SHOWING_RULES) {
        return { showCarousel: false, showRulesPanel: true }
    }
    if (p === IN_CHALLENGE_PHASE.ROLLING && spinBridge) {
        return { showCarousel: false, showRulesPanel: true }
    }
    if (p === IN_CHALLENGE_PHASE.ROLLING || p === IN_CHALLENGE_PHASE.WAITING_GM_CHALLENGE) {
        return { showCarousel: true, showRulesPanel: false }
    }
    return { showCarousel: false, showRulesPanel: false }
}

/** @deprecated Prefer `inChallengePhaseIs(..., IN_CHALLENGE_PHASE.SHOWING_RULES)`. */
export function isCommonSensePublicRulesPhase(inChallengeStatus: unknown): boolean {
    return inChallengePhaseIs(inChallengeStatus, IN_CHALLENGE_PHASE.SHOWING_RULES)
}

export function isCommonSenseFinalRanking(status: unknown): boolean {
    return Number(status ?? 0) === COMMON_SENSE_GAME_STATUS.FINISHED
}

/** Treat any non-idle session, including final ranking and legacy `status: 3` (pause), as active. */
export function isCommonSenseGameStarted(status: unknown): boolean {
    return Number(status ?? 0) !== COMMON_SENSE_GAME_STATUS.IDLE
}

/** Per-challenge roulette start counts from `CommonSenseData.player_data.challenge_start_counts`. */
export function resolveCommonSenseChallengeStartCounts(
    commonSenseData: CommonSenseData | null | undefined,
    extraRows?: CommonSenseExtraTeamInformation[]
): Record<number, number> {
    const out: Record<number, number> = {}
    const pdata = commonSenseData?.player_data
    if (pdata && typeof pdata === "object" && !Array.isArray(pdata)) {
        const raw = (pdata as Record<string, unknown>).challenge_start_counts
        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
                const id = Number(key)
                const count = Number(value)
                if (Number.isFinite(id) && id > 0 && Number.isFinite(count) && count > 0) {
                    out[id] = count
                }
            }
        }
    }

    const rows = Array.isArray(extraRows) ? extraRows : []
    for (const row of rows) {
        const answers = row?.data?.answers
        if (!answers || typeof answers !== "object" || Array.isArray(answers)) continue
        const runsByChallenge = new Map<number, Set<number>>()
        for (const key of Object.keys(answers as Record<string, unknown>)) {
            const [challengePart, runPart] = key.split(":")
            const challengeId = Number(challengePart)
            const playRun = Number(runPart)
            if (!Number.isFinite(challengeId) || challengeId <= 0) continue
            if (!Number.isFinite(playRun) || playRun < 0) continue
            let runs = runsByChallenge.get(challengeId)
            if (!runs) {
                runs = new Set<number>()
                runsByChallenge.set(challengeId, runs)
            }
            runs.add(playRun)
        }
        for (const [challengeId, runs] of runsByChallenge) {
            const inferred = runs.size
            out[challengeId] = Math.max(out[challengeId] ?? 0, inferred)
        }
    }

    return out
}

/** Deep bases for player / public shells (instructor UI is excluded). */
const COMMON_SENSE_AUDIENCE_BG_0 = "#010106"
const COMMON_SENSE_AUDIENCE_BG_1 = "#03050c"
const COMMON_SENSE_DEFAULT_PRIMARY = "#10b981"

function commonSenseToHexColor(input: unknown): string | null {
    if (typeof input !== "string") return null
    const value = input.trim()
    if (!value) return null
    if (/^#[0-9a-fA-F]{6}$/.test(value)) return value
    return null
}

function commonSenseHexToRgb(hex: string): { r: number; g: number; b: number } {
    const h = hex.replace("#", "")
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    }
}

function commonSenseRgbToHex(r: number, g: number, b: number): string {
    const toHex = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function commonSenseMixHex(hexA: string, hexB: string, weightB: number): string {
    const a = commonSenseHexToRgb(hexA)
    const b = commonSenseHexToRgb(hexB)
    const w = Math.max(0, Math.min(1, weightB))
    return commonSenseRgbToHex(
        a.r * (1 - w) + b.r * w,
        a.g * (1 - w) + b.g * w,
        a.b * (1 - w) + b.b * w
    )
}

function commonSenseRgba(hex: string, alpha: number): string {
    const { r, g, b } = commonSenseHexToRgb(hex)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function commonSenseLuminance(hex: string): number {
    const { r, g, b } = commonSenseHexToRgb(hex)
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

/**
 * Active CMS setting for theming: merge event payload with `common_sense_setting` content
 * so `font_family` / colors resolve when `setting` is an id or a partial object.
 */
export function resolveCommonSenseSettingRecord(
    commonSenseData: CommonSenseData | null | undefined,
    settings: CommonSenseSetting[]
): Record<string, unknown> | null {
    const raw = commonSenseData?.setting ?? null
    let embedded: Record<string, unknown> | null = null
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        embedded = raw as unknown as Record<string, unknown>
    }

    const settingId =
        embedded?.id != null
            ? Number(embedded.id)
            : typeof raw === "number" || typeof raw === "string"
              ? Number(raw)
              : NaN

    const fromContent =
        Number.isFinite(settingId) && settings.length > 0
            ? settings.find((x) => Number(x?.id) === settingId)
            : undefined

    const contentRecord = fromContent
        ? (fromContent as unknown as Record<string, unknown>)
        : null

    if (contentRecord && embedded) {
        return { ...contentRecord, ...embedded }
    }
    if (contentRecord) return contentRecord
    if (embedded) return embedded
    return null
}

/** Primary color from a CMS setting record (player / public / live display). */
export function resolveCommonSensePrimaryColorFromSetting(
    setting: Record<string, unknown> | null | undefined
): string {
    return commonSenseToHexColor(setting?.primary_color) ?? COMMON_SENSE_DEFAULT_PRIMARY
}

/** Readable primary on dark shells (same rules as `useCommonSenseTheme`). */
export function resolveCommonSensePrimaryColorSafe(primaryHex: string): string {
    const lum = commonSenseLuminance(primaryHex)
    if (lum > 0.82) return commonSenseMixHex(primaryHex, "#0f172a", 0.28)
    if (lum > 0.72) return commonSenseMixHex(primaryHex, "#0f172a", 0.16)
    return primaryHex
}

/**
 * Layered neon nebula background for player + public views.
 * First layer in the list = top (CSS). Last layer = base diagonal gradient.
 */
export function buildCommonSenseAudienceShellBackground(primaryHex: string): string {
    const raw = commonSenseToHexColor(primaryHex) ?? COMMON_SENSE_DEFAULT_PRIMARY
    const primary = resolveCommonSensePrimaryColorSafe(raw)
    const highlight = commonSenseMixHex(raw, "#5eead4", 0.14)
	const sky = commonSenseMixHex(raw, COMMON_SENSE_AUDIENCE_BG_1, 0.94)
	const midTone = commonSenseMixHex(primary, COMMON_SENSE_AUDIENCE_BG_1, 0.9)
	const floor = commonSenseMixHex(primary, COMMON_SENSE_AUDIENCE_BG_0, 0.97)
    const violet = commonSenseMixHex(raw, "#8b5cf6", 0.45)
    const rose = commonSenseMixHex(raw, "#f43f5e", 0.35)
    const cyan = commonSenseMixHex(raw, "#38bdf8", 0.4)

    const base = `linear-gradient(
        145deg,
        ${sky} 0%,
        ${midTone} 38%,
        ${floor} 100%
    )`

    const blooms = [
		`radial-gradient(ellipse 130% 95% at 50% -18%, ${commonSenseRgba(highlight, 0.26)}, transparent 48%)`,
		`radial-gradient(ellipse 75% 58% at 0% 0%, ${commonSenseRgba(violet, 0.18)}, transparent 44%)`,
		`radial-gradient(ellipse 70% 52% at 100% 8%, ${commonSenseRgba(rose, 0.14)}, transparent 42%)`,
		`radial-gradient(ellipse 90% 70% at 100% 100%, ${commonSenseRgba(raw, 0.18)}, transparent 52%)`,
		`radial-gradient(ellipse 85% 65% at 0% 100%, ${commonSenseRgba(cyan, 0.12)}, transparent 50%)`,
		`radial-gradient(ellipse 55% 45% at 72% 28%, ${commonSenseRgba(raw, 0.09)}, transparent 55%)`,
    ]

    return [...blooms, base].join(", ")
}

export type CommonSenseAudienceShellStyleOptions = {
    primaryHex: string
}

/** Inline shell style for player / public root containers (gradient only, no CMS bg image). */
export function buildCommonSenseAudienceShellStyle(
    options: CommonSenseAudienceShellStyleOptions
): Record<string, string> {
    const background = buildCommonSenseAudienceShellBackground(options.primaryHex)

    return {
        backgroundColor: COMMON_SENSE_AUDIENCE_BG_0,
        backgroundImage: background,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
    }
}

function readReactionSetIdFromSettingRecord(record: Record<string, unknown> | null | undefined): string | null {
    if (!record) return null
    const direct = record.reaction_set
    if (typeof direct === "string" && direct.trim()) return direct.trim()
    return null
}

const { t } = i18n.global

const createDefaultActivityConfiguration = (): CommonSenseActivityConfiguration => ({
    setting: "",
    modality: "IN_PERSON",
    team_mode: "PRECREATED",
    challenges: [],
    flow_mode_enabled: false,
    flow_order: [],
    flow_boosters: {},
    flow_source: "event",
})

export const useCommonSenseStore = defineStore("common_sense", {
    state: () => ({
        commonSenseData: {} as CommonSenseData,
        /** Incremented when WS pushes a CommonSenseUpdate that changes roulette-relevant fields (public display). */
        commonSenseWsRouletteTick: 0,
        settingsOptions: [] as CommonSenseSettingOption[],
        challengeOptions: [] as CommonSenseChallengeOption[],
        /**
         * Public roulette only: after the reel animation ends while `in_challenge_status` is still ROLLING,
         * show rules until the host sets SHOWING_RULES. Not persisted on the server.
         */
        publicRouletteSpinCompleted: false,
        /**
         * Per-team Common Sense payload (answers in `data`, nested `team`). Mirrors backend `ExtraTeamInformation`.
         * Populated by `TeamWs` when `eventType === "CS"` (see `team_ws.ts`).
         */
        extraTeamInformation: [] as CommonSenseExtraTeamInformation[],
        /** Resolved catalog for the active reaction set. */
        reactionsCatalog: [] as CommonSenseReaction[],
        /** Ephemeral bubbles shown on public / ranking rows. */
        floatingReactions: [] as CommonSenseFloatingReaction[],
        /** "+N" bubbles when round points are scored (player). */
        floatingPoints: [] as CommonSenseFloatingPoint[],
        /** One float per team/round answer key. */
        floatingPointsShownKeys: [] as string[],
        /** Dedupe WS echo after optimistic send (same client). */
        floatingReactionDedupeKey: "",
        floatingReactionDedupeAt: 0,
        /** Common Sense game WebSocket (player sends reactions here). */
        ws: null as WebSocket | null,
        /** Local-only mode used by CMS/client previews. Skips API refreshes and WebSocket work. */
        previewMode: false,
    }),
    getters: {
        /** Mirrors `CommonSenseData.status` (session lifecycle). */
        status(): number {
            return Number(this.commonSenseData?.status ?? 0)
        },
        /** Mirrors `CommonSenseData.in_challenge_status` from API / WebSocket. */
        in_challenge_status(): number {
            return Number(this.commonSenseData?.in_challenge_status ?? 0)
        },
        /** Mirrors `CommonSenseData.challenge_play_subphase`. */
        challenge_play_subphase(): number {
            return Number(this.commonSenseData?.challenge_play_subphase ?? 0)
        },
        challenge_play_run(): number {
            return Number(this.commonSenseData?.challenge_play_run ?? 0)
        },
        activeChallengeId(): number | null {
            return resolveActiveChallengeId(this.commonSenseData?.challenge_active)
        },
        /** Full challenge row: embedded `challenge_active` or content lookup by id. */
        activeChallenge(): Record<string, unknown> | null {
            const active = this.commonSenseData?.challenge_active
            if (active && typeof active === "object" && active !== null && "id" in active) {
                return active as Record<string, unknown>
            }
            const id = this.activeChallengeId
            if (id == null) return null
            const list = ((useContentStore().content as { common_sense_challenge?: Record<string, unknown>[] })
                .common_sense_challenge ?? []) as Record<string, unknown>[]
            return list.find((x) => Number(x?.id) === id) ?? null
        },
        activeChallengeType(): CommonSenseChallengeType {
            return resolveChallengeTypeFromChallenge(this.activeChallenge)
        },
        activeChallengeLabel(): string {
            const record = this.activeChallenge
            if (!record) return ""
            const fromTranslated = getTranslatedObject(record as object, "name")
            if (typeof fromTranslated === "string" && fromTranslated.trim()) return fromTranslated.trim()
            const nid = record.name_id
            if (typeof nid === "string" && nid.trim()) return nid.trim()
            const id = this.activeChallengeId
            return id != null ? String(id) : ""
        },
        activeChallengeData(): unknown {
            const record = this.activeChallenge
            if (!record) return null
            const eventStore = useEventsStore()
            return resolveChallengeDataForEventLanguage(record, eventStore.eventSelected?.language)
        },
        activeChallengeSubphaseTotal(): number {
            return countCommonSenseChallengeSubphases(this.activeChallengeData)
        },
        challengePhaseTimer(): CommonSenseTimer | null {
            const timer = this.commonSenseData?.timer
            return timer && typeof timer === "object" ? timer : null
        },
        /** UTQ `getSecondsRoundRemaining`: countdown target from `CommonTimer.finish_round_time`. */
        getChallengePhaseTimerFinish(): Date | undefined {
            const timer = this.challengePhaseTimer
            if (timer?.finish_round_time == null || timer.finish_round_time === "") {
                return undefined
            }
            return new Date(timer.finish_round_time)
        },
        showChallengePhaseTimer(): boolean {
            const phase = this.in_challenge_status
            const hasFinish = Boolean(this.challengePhaseTimer?.finish_round_time)
            return (
                hasFinish &&
                (phase === IN_CHALLENGE_PHASE.CHALLENGE_PLAYING ||
                    phase === IN_CHALLENGE_PHASE.CHALLENGE_RESULT)
            )
        },
        publicShowCarousel(): boolean {
            return resolvePublicChallengeDisplayFlags(this.commonSenseData?.in_challenge_status, {
                rouletteSpinCompletedForPublic: this.publicRouletteSpinCompleted,
            }).showCarousel
        },
        publicShowRulesPanel(): boolean {
            return resolvePublicChallengeDisplayFlags(this.commonSenseData?.in_challenge_status, {
                rouletteSpinCompletedForPublic: this.publicRouletteSpinCompleted,
            }).showRulesPanel
        },
        modalityOptions: () => [
            { label: t("gm.dashboards.online"), value: "ONLINE" },
            { label: t("gm.dashboards.presential"), value: "IN_PERSON" },
            { label: t("gm.dashboards.hybrid"), value: "HYBRID" },
        ],
        teamModeOptions: () => [
            {
                value: "ON_THE_FLY" as const,
                label: t("gm.dashboards.team_mode_on_the_fly"),
                description: t("gm.dashboards.team_mode_on_the_fly_desc"),
            },
            {
                value: "PRECREATED" as const,
                label: t("gm.dashboards.team_mode_precreated"),
                description: t("gm.dashboards.team_mode_precreated_desc"),
            },
        ],
        activityFormRules: () => ({
            setting: {
                required: true,
                trigger: "change",
                message: t("gm.dashboards.placeholder_type"),
            },
            modality: {
                required: true,
                trigger: "change",
                message: t("gm.dashboards.type_game"),
            },
            challenges: {
                type: "array" as const,
                required: true,
                trigger: "change" as const,
                message: t("gm.dashboards.select_challenge"),
            },
        }),
        activityConfiguration(): CommonSenseActivityConfiguration {
            return useEventsStore().activityConfiguration as CommonSenseActivityConfiguration
        },
        settings(): CommonSenseSetting[] {
            return ((useContentStore().content as any).common_sense_setting ?? []) as CommonSenseSetting[]
        },
        selectedSetting(): CommonSenseSetting | null {
            const settingId = Number(this.activityConfiguration.setting)
            if (!Number.isFinite(settingId) || settingId <= 0) return null
            return this.settings.find((setting) => setting.id === settingId) ?? null
        },
        resolvedActiveSetting(): Record<string, unknown> | null {
            return resolveCommonSenseSettingRecord(this.commonSenseData, this.settings)
        },
        flowModeEnabled(): boolean {
            return Boolean(this.commonSenseData?.flow_mode_enabled)
        },
        effectiveFlowOrder(): number[] {
            const selectedIds = normalizeCommonSenseChallengeIds(this.commonSenseData?.challenges ?? [])
            const dataOrder = normalizeCommonSenseChallengeIds(this.commonSenseData?.flow_order ?? [])
            if (dataOrder.length > 0) {
                const setting = this.resolvedActiveSetting as { questions_choose?: boolean } | null
                return resolveCommonSenseFlowOrder(dataOrder, selectedIds, {
                    prependMissing: Boolean(setting?.questions_choose),
                })
            }
            const setting = this.resolvedActiveSetting as { flow_order?: unknown; questions_choose?: boolean } | null
            return resolveCommonSenseFlowOrder(setting?.flow_order ?? [], selectedIds, {
                prependMissing: Boolean(setting?.questions_choose),
            })
        },
        effectiveFlowBoosters(): Record<string, string> {
            const selectedIds = normalizeCommonSenseChallengeIds(this.commonSenseData?.challenges ?? [])
            const dataBoosters = normalizeCommonSenseFlowBoosters(this.commonSenseData?.flow_boosters ?? {}, selectedIds)
            if (Object.keys(dataBoosters).length > 0) return dataBoosters
            const setting = this.resolvedActiveSetting as { flow_boosters?: unknown } | null
            return normalizeCommonSenseFlowBoosters(setting?.flow_boosters ?? {}, selectedIds)
        },
        flowRuntimeQueue(): number[] {
            return normalizeCommonSenseChallengeIds(this.commonSenseData?.flow_runtime_queue ?? [])
        },
        /** Next challenge id in Flow mode while waiting on the home screen (before roulette spin). */
        nextFlowQueuedChallengeId(): number | null {
            if (!this.flowModeEnabled) return null
            const nextId = this.flowRuntimeQueue[0]
            return Number.isFinite(nextId) ? nextId : null
        },
        /** Reaction package id selected in event setting; defaults to `default`. */
        activeReactionSetId(): string {
            const fromSetting = readReactionSetIdFromSettingRecord(this.resolvedActiveSetting)
            return fromSetting || "default"
        },
        /** Eight slots from the setting-selected reactions package. */
        activeReactionsCatalog(): CommonSenseReaction[] {
            if (this.reactionsCatalog.length > 0) {
                return this.reactionsCatalog
            }
            return resolveReactionSetById(this.activeReactionSetId)
        },
        getReactionById(): (id: string) => CommonSenseReaction | undefined {
            return (id: string) => this.activeReactionsCatalog.find((r) => r.id === id)
        },
        /**
         * All `ExtraTeamInformation` rows for ranking: merge WS list + per-team updates.
         * Players only subscribe to their team channel; public/instructor use `team_all`.
         */
        mergedExtraTeamInformation(): CommonSenseExtraTeamInformation[] {
            const eventStore = useEventsStore()
            const fromEvent = eventStore.full_team_information
            const fromWs = Array.isArray(fromEvent) ? fromEvent : []
            const fromStore = this.extraTeamInformation
            const byTeamId = new Map<number, CommonSenseExtraTeamInformation>()
            for (const raw of [...fromWs, ...fromStore]) {
                if (!raw || typeof raw !== "object") continue
                const row = raw as CommonSenseExtraTeamInformation
                const teamObj = row.team as { id?: number } | undefined
                const teamId = teamObj?.id != null ? Number(teamObj.id) : null
                if (teamId == null || !Number.isFinite(teamId)) continue
                byTeamId.set(teamId, row)
            }
            return [...byTeamId.values()]
        },
    },
    actions: {
        resetActivityForm() {
            useEventsStore().activityConfiguration = createDefaultActivityConfiguration()
            this.settingsOptions = []
            this.challengeOptions = []
        },
        resetPreviewState() {
            this.previewMode = false
            this.commonSenseData = {} as CommonSenseData
            this.commonSenseWsRouletteTick = 0
            this.publicRouletteSpinCompleted = false
            this.extraTeamInformation = []
            this.floatingReactions = []
            this.floatingPoints = []
            this.floatingPointsShownKeys = []
        },
        initializeActivityForm(activityFormRef: unknown) {
            useEventsStore().activityConfiguration = {
                ...createDefaultActivityConfiguration(),
                activity_formRef: activityFormRef,
            }
        },
        syncJoinLobbyFromTeamMode() {
            const eventsStore = useEventsStore()
            const event = eventsStore.eventSelected as { join_lobby_enabled?: boolean } | null
            if (!event) return
            if (this.commonSenseData?.team_mode === "ON_THE_FLY" && !event.join_lobby_enabled) {
                event.join_lobby_enabled = true
            }
        },
        async getCommonSenseData(api_key: string) {
            await simpleAxios("get", "events/event_full/" + api_key, {})
                .then((response: AxiosResponse<CommonSenseData>) => {
                    syncServerClockFromPayload(response.data)
                    this.commonSenseData = response.data as CommonSenseData
                    this.publicRouletteSpinCompleted = false
                    this.syncJoinLobbyFromTeamMode()
                })
                .catch((error: unknown) => {
                    console.log(error)
                })
        },
        async loadActivitySetup(apiKey?: string, activityFormRef?: unknown) {
            const contentStore = useContentStore()
            const eventsStore = useEventsStore()

            this.initializeActivityForm(activityFormRef)

            const requests = [
                contentStore.getAllMultipleContent(["common_sense_setting", "common_sense_challenge"]),
            ] as Promise<void>[]

            if (apiKey) {
                requests.push(eventsStore.getEventFull(apiKey), this.getCommonSenseData(apiKey))
            }

            await fetchPromiseAll(requests)

            this.settingsOptions = this.settings.map((setting) => ({
                label: setting.name,
                value: setting.id.toString(),
            }))

            if (apiKey) {
                this.applyCommonSenseDataToForm()
            }
        },
        getSettingChallengeIds(setting?: CommonSenseSetting) {
            if (!setting) {
                return []
            }

            const clientPool = setting.challenges_client ?? []
            if (clientPool.length) {
                return clientPool.map((challenge) =>
                    typeof challenge === "number" ? challenge : challenge.id
                )
            }

            if (!setting.challenges?.length) {
                return []
            }

            return setting.challenges.map((challenge) =>
                typeof challenge === "number" ? challenge : challenge.id
            )
        },
        buildChallengeOptions(challengeIds: number[]) {
            const pool = ((useContentStore().content as any).common_sense_challenge ?? []) as any[]
            return normalizeCommonSenseChallengeIds(challengeIds)
                .map((id) => pool.find((challenge: { id: number }) => Number(challenge.id) === id))
                .filter((challenge): challenge is { id: number } => Boolean(challenge))
                .map((challenge) => ({
                    value: challenge.id,
                    label: getTranslatedObject(challenge, "name"),
                }))
        },
        refreshChallengeOptions() {
            const settingId = Number(this.activityConfiguration.setting)
            const selectedSetting = this.settings.find((setting) => setting.id === settingId)
            this.challengeOptions = this.buildChallengeOptions(this.getSettingChallengeIds(selectedSetting))
        },
        onSettingChange() {
            const settingId = Number(this.activityConfiguration.setting)
            const selectedSetting = this.settings.find((setting) => setting.id === settingId)
            this.refreshChallengeOptions()

            const allowedChallengeIds = this.challengeOptions.map((option) => option.value)
            const selectedChallenges = this.activityConfiguration.challenges ?? []
            const filteredChallenges = selectedChallenges.filter((challengeId) =>
                allowedChallengeIds.includes(challengeId)
            )

            this.activityConfiguration.challenges = filteredChallenges.length
                ? filteredChallenges
                : [...allowedChallengeIds]
            this.activityConfiguration.flow_order = resolveCommonSenseFlowOrder(
                selectedSetting?.flow_order ?? this.activityConfiguration.flow_order ?? [],
                this.activityConfiguration.challenges,
                { prependMissing: Boolean(selectedSetting?.questions_choose) }
            )
            this.activityConfiguration.flow_boosters = normalizeCommonSenseFlowBoosters(
                selectedSetting?.flow_boosters ?? this.activityConfiguration.flow_boosters ?? {},
                this.activityConfiguration.challenges
            )
            this.activityConfiguration.flow_mode_enabled = Boolean(selectedSetting?.flow_mode_enabled)
            this.activityConfiguration.flow_source = "event"
        },
        async applySettingLogoToEvent(
            onConflict?: () => Promise<"keep" | "apply">
        ): Promise<void> {
            const setting = this.selectedSetting
            const logoUrl = setting?.logo
            if (!logoUrl || typeof logoUrl !== "string") return

            const eventsStore = useEventsStore()
            const bridge = eventsStore.eventLogoBridge
            if (!bridge.applyLogo) return

            const applyLogo = async () => {
                const file = await fetchMediaAsFile(logoUrl)
                if (!file) return
                bridge.applyLogo!(file)
                this.activityConfiguration.apply_setting_logo = true
            }

            const hasLogo = bridge.hasLogo?.() ?? false
            if (!hasLogo) {
                await applyLogo()
                return
            }

            const choice = onConflict ? await onConflict() : "keep"
            if (choice === "apply") {
                await applyLogo()
            }
        },
        applyCommonSenseDataToForm() {
            const data = this.commonSenseData

            if (data.setting) {
                this.activityConfiguration.setting = String(
                    typeof data.setting === "object" ? data.setting.id : data.setting
                )
            }

            this.activityConfiguration.modality = data.modality ?? "IN_PERSON"
            const eventsStore = useEventsStore()
            const joinLobbyEnabled = Boolean(
                (eventsStore.eventSelected as { join_lobby_enabled?: boolean } | null)?.join_lobby_enabled
            )
            this.activityConfiguration.team_mode = joinLobbyEnabled
                ? "ON_THE_FLY"
                : (data.team_mode ?? "PRECREATED")
            this.activityConfiguration.challenges = (data.challenges ?? []).map((challenge) =>
                typeof challenge === "number" ? challenge : challenge.id
            )
            this.activityConfiguration.flow_mode_enabled = Boolean(data.flow_mode_enabled)
            const dataSetting = typeof data.setting === "object" ? data.setting : null
            this.activityConfiguration.flow_order = resolveCommonSenseFlowOrder(
                data.flow_order ?? [],
                this.activityConfiguration.challenges,
                { prependMissing: Boolean(dataSetting?.questions_choose) }
            )
            this.activityConfiguration.flow_boosters = normalizeCommonSenseFlowBoosters(
                data.flow_boosters ?? {},
                this.activityConfiguration.challenges
            )
            this.activityConfiguration.flow_source = "event"
            this.refreshChallengeOptions()
        },
        async startGame(api_key: string) {
            await simpleAxios("post", `CommonSense/start_game/${api_key}`, {})
            await this.getCommonSenseData(api_key)
        },
        async showFinalRanking(api_key: string) {
            await simpleAxios("post", `CommonSense/show_final_ranking/${api_key}`, {})
            await this.getCommonSenseData(api_key)
        },
        async resumeGame(api_key: string) {
            await simpleAxios("post", `CommonSense/resume_game/${api_key}`, {})
            await this.getCommonSenseData(api_key)
        },
        async patchCommonSenseData(api_key: string, payload: Partial<CommonSenseData>) {
            await simpleAxios("put", `events/event_full/${api_key}`, payload)
            await this.getCommonSenseData(api_key)
        },
        async updateFlow(api_key: string, payload: { flow_mode_enabled: boolean; flow_order: number[]; flow_boosters?: Record<string, string | null | undefined> }) {
            await simpleAxios("put", `CommonSense/flow/${api_key}`, payload)
            await this.getCommonSenseData(api_key)
            await this.refreshExtraTeamInformation(api_key)
        },
        async advanceInChallengeStatus(
            api_key: string,
            payload?: { booster_active?: string | null }
        ) {
            await simpleAxios(
                "post",
                `CommonSense/advance_in_challenge_status/${api_key}`,
                payload ?? {}
            )
            await this.getCommonSenseData(api_key)
            await this.refreshExtraTeamInformation(api_key)
        },
        async refreshExtraTeamInformation(api_key: string) {
            if (this.previewMode) return
            try {
                const response = await simpleAxios("get", `CommonSense/teams_extra/${api_key}`, {})
                const rows = (response as AxiosResponse<unknown>)?.data
                if (Array.isArray(rows)) {
                    this.setExtraTeamInformation(rows)
                }
            } catch (error: unknown) {
                console.warn("[common_sense] refreshExtraTeamInformation failed", error)
            }
        },
        async finishChallenge(api_key: string) {
            await simpleAxios("post", `CommonSense/finish_challenge/${api_key}`, {})
            await this.refreshExtraTeamInformation(api_key)
            await this.getCommonSenseData(api_key)
        },
        async resetToWaitingChallenge(api_key: string) {
            await simpleAxios("post", `CommonSense/reset_to_waiting_challenge/${api_key}`, {})
            this.setPublicRouletteSpinCompleted(false)
            await this.getCommonSenseData(api_key)
        },
        async skipFlowChallenge(api_key: string) {
            await simpleAxios("post", `CommonSense/skip_flow_challenge/${api_key}`, {})
            this.setPublicRouletteSpinCompleted(false)
            await this.getCommonSenseData(api_key)
            await this.refreshExtraTeamInformation(api_key)
        },
        async setInChallengeStatus(api_key: string, status: number) {
            await this.patchCommonSenseData(api_key, { in_challenge_status: status } as Partial<CommonSenseData>)
        },
        setPublicRouletteSpinCompleted(value: boolean) {
            this.publicRouletteSpinCompleted = value
        },
        /** Full list from `team_all` snapshot or merged list after single-team `CS` WS messages. */
        setReactionsCatalog(reactions: CommonSenseReaction[] | null | undefined) {
            this.reactionsCatalog =
                reactions == null ? [] : resolveCommonSenseReactions(reactions)
        },
        floatingReactionDedupeKeyFor(reactionId: string, teamId: number | null) {
            return `${reactionId}:${teamId ?? ""}`
        },
        markFloatingReactionDedupe(reactionId: string, teamId: number | null) {
            this.floatingReactionDedupeKey = this.floatingReactionDedupeKeyFor(reactionId, teamId)
            this.floatingReactionDedupeAt = Date.now()
        },
        shouldSkipFloatingReactionWs(reactionId: string, teamId: number | null): boolean {
            const key = this.floatingReactionDedupeKeyFor(reactionId, teamId)
            return (
                key === this.floatingReactionDedupeKey &&
                Date.now() - this.floatingReactionDedupeAt < 400
            )
        },
        shouldShowFloatingPointsForKey(key: string): boolean {
            return !this.floatingPointsShownKeys.includes(key)
        },
        markFloatingPointsShown(key: string) {
            if (this.floatingPointsShownKeys.includes(key)) return
            this.floatingPointsShownKeys.push(key)
            if (this.floatingPointsShownKeys.length > 48) {
                this.floatingPointsShownKeys.splice(
                    0,
                    this.floatingPointsShownKeys.length - 48
                )
            }
        },
        pushFloatingPoint(payload: { points: number; teamId: number }) {
            const points = Math.round(Number(payload.points))
            if (!Number.isFinite(points) || points <= 0) return
            const entry: CommonSenseFloatingPoint = {
                uid: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                points,
                place: Math.floor(Math.random() * (72 - 28) + 28),
                teamId: payload.teamId,
            }
            this.floatingPoints.push(entry)
            window.setTimeout(() => {
                const idx = this.floatingPoints.findIndex((p) => p.uid === entry.uid)
                if (idx !== -1) this.floatingPoints.splice(idx, 1)
            }, 3000)
        },
        pushFloatingReaction(payload: { reactionId: string; teamId?: number | null }) {
            const teamId =
                payload.teamId != null && Number.isFinite(Number(payload.teamId))
                    ? Number(payload.teamId)
                    : null
            const entry: CommonSenseFloatingReaction = {
                uid: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                reactionId: payload.reactionId,
                place: Math.floor(Math.random() * (75 - 25) + 25),
                teamId,
            }
            this.floatingReactions.push(entry)
            window.setTimeout(() => {
                const idx = this.floatingReactions.findIndex((r) => r.uid === entry.uid)
                if (idx !== -1) {
                    this.floatingReactions.splice(idx, 1)
                }
            }, 3000)
        },
        sendReaction(reactionId: string, teamId: number | null = null) {
            const now = Date.now()
            const dedupeKey = `${reactionId}:${teamId ?? ""}`
            if (lastReactionSend && lastReactionSend.key === dedupeKey && now - lastReactionSend.at < 500) {
                return Promise.resolve()
            }
            lastReactionSend = { key: dedupeKey, at: now }
            this.markFloatingReactionDedupe(reactionId, teamId)
            this.pushFloatingReaction({ reactionId, teamId })

            const payload = JSON.stringify({
                action: "reaction",
                reaction: reactionId,
                team_id: teamId,
            })

            const sendOnSocket = (ws: WebSocket) => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(payload)
                }
            }

            let ws = this.ws
            if (!ws || ws.readyState === WebSocket.CLOSED) {
                const apiKey = useEventsStore().eventSelected?.api_key
                if (apiKey) {
                    void import("@/utils/ws/common_sense_ws").then(({ commonSenseWs }) => {
                        commonSenseWs(String(apiKey))
                        const reopened = this.ws
                        if (!reopened) return
                        if (reopened.readyState === WebSocket.OPEN) {
                            sendOnSocket(reopened)
                            return
                        }
                        if (reopened.readyState === WebSocket.CONNECTING) {
                            const onOpen = () => {
                                reopened.removeEventListener("open", onOpen)
                                sendOnSocket(reopened)
                            }
                            reopened.addEventListener("open", onOpen)
                        }
                    })
                }
                return Promise.resolve()
            }

            if (ws.readyState === WebSocket.CONNECTING) {
                const onOpen = () => {
                    ws?.removeEventListener("open", onOpen)
                    if (ws) sendOnSocket(ws)
                }
                ws.addEventListener("open", onOpen)
                return Promise.resolve()
            }

            sendOnSocket(ws)
            return Promise.resolve()
        },
        setExtraTeamInformation(payload: unknown) {
            if (Array.isArray(payload)) {
                // Shallow clone so Pinia reactivity fires when WS mutates eventStore.full_team_information in place.
                this.extraTeamInformation = [
                    ...(payload as CommonSenseExtraTeamInformation[]),
                ]
                return
            }
            if (payload && typeof payload === "object") {
                const row = payload as CommonSenseExtraTeamInformation
                if (row.id == null) return
                const list = [...this.extraTeamInformation]
                const idx = list.findIndex((r) => r.id === row.id)
                if (idx !== -1) {
                    list[idx] = row
                } else {
                    list.push(row)
                }
                this.extraTeamInformation = list
            }
        },
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCommonSenseStore, import.meta.hot))
}
